import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { validateEmail, validatePhone } from "../shared/utils/validation";
import ConfirmationModal from "../components/ConfirmationModal";
import ColumnCustomizerModal from "../components/ColumnCustomizerModal";
import UserAdminModal from "../components/UserAdminModal";
import UserEmailSwapModal from "../components/UserEmailSwapModal";
import { SortableTableTh, TableActionColumnHeader } from "../components/TableShell";
import { IconEdit, IconDelete, IconInvite, IconSwapEmail, IconVerificationCode, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import SearchableSelect from "../components/SearchableSelect";
import { formatPhoneDisplay } from "../shared/utils/phoneFormat";
import { useTableSort } from "../shared/hooks/useTableSort";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";

import { apiFetch } from "../shared/api/apiFetch";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import ImportCsvPanel from "../components/entity/ImportCsvPanel";
import { parseCsvTextToNormalizedTable, findCsvColumnIndex } from "../shared/utils/csvParse";
import { attachValidatedCsvFileImport, downloadCsvTextFile, downloadCsvTextFromString } from "../shared/utils/csvFileHelpers";
import {
  USER_ADMIN_CSV_IMPORT_HEADER_LINE,
  buildUserAdminCsvExportText,
} from "../shared/utils/userAdminCsv";

const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const { fetchTablePreferences, saveTablePreferences } = require('../shared/api/tablePreferences');
const { inviteUser: inviteUserApi } = require('../shared/api/userInvite');
const { swapUserEmail: swapUserEmailApi } = require('../shared/api/userEmailSwap');
const { sendVerificationCode: sendVerificationCodeApi } = require('../shared/api/userVerificationCode');
const { displayEmailOrDash, isPlaceholderEmail } = require('../shared/utils/displayEmail');
const ErrorBanner = require('../components/ErrorBanner').default;

const USER_ADMIN_TABLE_COLUMNS = [
  { id: 'name', label: 'Name' },
  { id: 'email_id', label: 'Email ID' },
  { id: 'roles', label: 'Roles' },
  { id: 'login_status', label: 'Login Status' },
  { id: 'last_login', label: 'Last Login' },
  { id: 'email_verified', label: 'Verified' },
  { id: 'phone', label: 'Phone number' },
  { id: 'created', label: 'Created' },
];
/** Default visible (starting point); Phone / Created via column customizer. */
const USER_ADMIN_TABLE_DEFAULT_VISIBLE = [
  'name',
  'email_id',
  'roles',
  'login_status',
  'last_login',
  'email_verified',
];
const USER_ADMIN_TABLE_KEY = 'user_admin.table';
const USER_ADMIN_SORT_KEY_TYPES = {
  last_login: 'date',
  created: 'date',
};

function formatUserTableName(o) {
  const parts = [o.first_name, o.last_name]
    .map((x) => (x != null ? String(x).trim() : ''))
    .filter(Boolean);
  return parts.length ? parts.join(' ') : '—';
}

function getUserAdminDisplayRoles(o) {
  return o.roles && o.roles.length ? o.roles : (o.role ? [o.role] : []);
}

function getLinkedParentUserId(user) {
  const pid = user?.parent_user_id;
  if (pid == null) return '';
  return String(pid).trim();
}

function userHasParentRole(user) {
  return getUserAdminDisplayRoles(user).includes('Parent');
}

const USER_ADMIN_REL_CHILDREN = 'children';
const USER_ADMIN_REL_PARENT = 'parent';

function getUserAdminSortValue(o, key) {
  switch (key) {
    case 'name':
      return formatUserTableName(o);
    case 'roles':
      return getUserAdminDisplayRoles(o).join(', ');
    case 'login_status':
      return o.login_status ?? 'No Login';
    case 'phone':
      return o.phone_number != null ? String(o.phone_number) : '';
    case 'created':
      return o.created_date;
    case 'email_verified':
      return o.email_verified ? 'Yes' : 'No';
    case 'email_id':
      return o.email_id != null ? String(o.email_id) : '';
    case 'last_login':
      return o.last_login;
    default:
      return o[key];
  }
}

/** Sort without mutating display fields (roles array, email_verified boolean, etc.). */
function sortUserAdminRows(rows, sortConfig) {
  if (!sortConfig?.key) return rows;
  const key = sortConfig.key;
  const decorated = rows.map((o) => ({ __orig: o, [key]: getUserAdminSortValue(o, key) }));
  return sortRowsByConfig(decorated, sortConfig, USER_ADMIN_SORT_KEY_TYPES).map((r) => r.__orig);
}

function UserAdmin() {
  // set state
  const [CurrentObjs, setCurrentObjs] = useState([]);
  const [RoleObjs, setRoleObjs] = useState([]);

  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo.account_id;  
  
  const serverUrl = uiConfig.serverInfo.serverUrl; 

  const [userModal, setUserModal] = useState({ open: false, mode: 'create', user: null });
  const [emailSwapModal, setEmailSwapModal] = useState({ open: false, user: null });
  const [bannerError, setBannerError] = useState('');
  const [inviteLoadingId, setInviteLoadingId] = useState(null);
  const [resendLoadingId, setResendLoadingId] = useState(null);
  const [verificationLoadingId, setVerificationLoadingId] = useState(null);
  const [emailSwapLoadingId, setEmailSwapLoadingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(null); // 'delete' | 'invite' | 'resend' | 'verification'
  const [filterLoginStatus, setFilterLoginStatus] = useState(''); // '' = All, 'No Login', 'Invited', 'Active'
  const [filterRole, setFilterRole] = useState(''); // '' = All, or role name
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importPanelExpanded, setImportPanelExpanded] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm", // "confirm" | "alert"
    confirmLabel: undefined,
    onConfirm: null,
  });
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState(() => [...USER_ADMIN_TABLE_DEFAULT_VISIBLE]);
  const [columnPrefsSaving, setColumnPrefsSaving] = useState(false);
  const { sortConfig, handleSort } = useTableSort({ key: 'name', direction: 'asc' });
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParentUserId = (searchParams.get('parentUserId') || '').trim();
  const filterRel = (searchParams.get('rel') || '').trim();
  const filterFromUserId = (searchParams.get('fromUserId') || '').trim();
  const hasRelationshipFilter = !!(
    filterParentUserId
    && (filterRel === USER_ADMIN_REL_CHILDREN || filterRel === USER_ADMIN_REL_PARENT)
  );

  const relationshipLookups = useMemo(() => {
    const usersById = new Map();
    const linkedChildCountByParentId = new Map();
    for (const user of CurrentObjs) {
      usersById.set(String(user._id), user);
      const parentId = getLinkedParentUserId(user);
      if (parentId) {
        linkedChildCountByParentId.set(
          parentId,
          (linkedChildCountByParentId.get(parentId) || 0) + 1
        );
      }
    }
    return { usersById, linkedChildCountByParentId };
  }, [CurrentObjs]);

  useEffect(() => {
    getDocument();
    getRoleList();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadPrefs() {
      if (!account_id || !userInfo?._id) return;
      try {
        const ids = await fetchTablePreferences(account_id, userInfo._id, USER_ADMIN_TABLE_KEY);
        if (cancelled) return;
        if (Array.isArray(ids) && ids.length > 0) {
          const allowed = new Set(USER_ADMIN_TABLE_COLUMNS.map((c) => c.id));
          const filtered = ids.filter((id) => allowed.has(id));
          if (filtered.length > 0) setVisibleColumnIds(filtered);
        }
      } catch (err) {
        // Non-blocking; keep defaults.
      }
    }
    loadPrefs();
    return () => {
      cancelled = true;
    };
  }, [account_id, userInfo?._id]);

  function getRoleList() {
    apiFetch(serverUrl + '/getUserRole', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(resp => resp.json())
      .then(data => setRoleObjs(data))
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  function getDocument() {
    apiFetch(serverUrl + '/getUser', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(resp => resp.json())
      .then(data => setCurrentObjs(data))
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  async function handleUserModalSubmit(payload) {
    const isEdit = payload.mode === 'edit';
    const genderVal = payload.gender != null ? String(payload.gender).trim() : '';
    const body = isEdit
      ? {
          account_id,
          _id: payload._id,
          first_name: payload.first_name,
          last_name: payload.last_name,
          phone_number: payload.phone_number,
          gender: genderVal,
          roles: payload.roles,
        }
      : {
          account_id,
          first_name: payload.first_name,
          last_name: payload.last_name,
          email_id: payload.email_id,
          phone_number: payload.phone_number,
          gender: genderVal,
          roles: payload.roles,
        };
    setBannerError('');
    try {
      const response = await apiFetch(serverUrl + (isEdit ? '/updateUser' : '/createUser'), {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await response.text();
      if (!response.ok) {
        setConfirmDialog({ open: true, title: 'User', message: data, mode: 'alert', onConfirm: null });
        throw new Error('');
      }
      setConfirmDialog({ open: true, title: 'User', message: data, mode: 'alert', onConfirm: null });
      getDocument();
      setUserModal({ open: false, mode: 'create', user: null });
    } catch (err) {
      if (err && typeof err.message === 'string' && err.message === '') {
        throw err;
      }
      const msg = getMessageForFetchError(err);
      setBannerError(msg);
      throw new Error(msg);
    }
  }

  function deleteDocument(currentObj) {
    const { _id } = currentObj;
    const nameLabel = [currentObj.first_name, currentObj.last_name].filter(Boolean).join(' ').trim();
    const emailLabel = displayEmailOrDash(currentObj.email_id);
    const label = nameLabel || (emailLabel !== '—' ? emailLabel : '') || 'this user';
    setConfirmDialog({
      open: true,
      title: 'Delete user',
      message: `Delete ${label}?\n\nThis also removes their login from authentication if present.\n\nThis cannot be undone.`,
      mode: 'confirm',
      confirmLabel: 'Delete',
      onConfirm: () => {
        apiFetch(serverUrl + '/deleteUser', {
          method: 'POST',
          body: JSON.stringify({ account_id, _id }),
        })
          .then(async (response) => {
            const data = await response.text();
            if (!response.ok) {
              const msg = (() => {
                try {
                  const j = JSON.parse(data);
                  return j.message || data;
                } catch {
                  return data;
                }
              })();
              setConfirmDialog({ open: true, title: 'Delete user', message: msg, mode: 'alert', onConfirm: null });
              return;
            }
            setConfirmDialog({ open: true, title: 'User', message: data, mode: 'alert', onConfirm: null });
            getDocument();
          })
          .catch(err => setBannerError(getMessageForFetchError(err)));
      },
    });
  }

  function inviteUser(currentObj) {
    const { _id } = currentObj;
    setInviteLoadingId(_id);
    inviteUserApi({ account_id, _id })
      .then((data) => {
        if (data && data.message) {
          setConfirmDialog({
            open: true,
            title: "Invite",
            message: data.message,
            mode: "alert",
            onConfirm: null,
          });
        }
        getDocument();
      })
      .catch((err) => setBannerError(err?.message || getMessageForFetchError(err)))
      .finally(() => setInviteLoadingId(null));
  }

  function resendInvite(currentObj) {
    const { _id } = currentObj;
    setResendLoadingId(_id);
    apiFetch(serverUrl + '/resendInvite', {
      method: 'POST',
      body: JSON.stringify({ account_id, _id }),
    })
      .then(resp => resp.json())
      .then(data => {
        if (data && data.message) setConfirmDialog({ open: true, title: "Invite", message: data.message, mode: "alert", onConfirm: null });
        getDocument();
      })
      .catch(err => setBannerError(getMessageForFetchError(err)))
      .finally(() => setResendLoadingId(null));
  }

  function sendVerificationCode(currentObj) {
    const { _id } = currentObj;
    setVerificationLoadingId(_id);
    sendVerificationCodeApi({ account_id, _id })
      .then((data) => {
        if (data && data.message) {
          const codeHint = data.verificationCode
            ? ` Code: ${data.verificationCode} (shown for testing).`
            : '';
          setConfirmDialog({
            open: true,
            title: "Verification code",
            message: `${data.message}${codeHint}`,
            mode: "alert",
            onConfirm: null,
          });
        }
      })
      .catch((err) => setBannerError(err?.message || getMessageForFetchError(err)))
      .finally(() => setVerificationLoadingId(null));
  }

  async function handleEmailSwapSubmit(newEmail) {
    const user = emailSwapModal.user;
    if (!user || !user._id) return;
    setEmailSwapLoadingId(user._id);
    setBannerError('');
    try {
      const data = await swapUserEmailApi({
        account_id,
        _id: user._id,
        new_email_id: newEmail,
      });
      setConfirmDialog({
        open: true,
        title: "Swap email",
        message: (data && data.message) || "Email updated.",
        mode: "alert",
        onConfirm: null,
      });
      getDocument();
      setEmailSwapModal({ open: false, user: null });
    } catch (err) {
      const msg = err?.message || getMessageForFetchError(err);
      throw new Error(msg);
    } finally {
      setEmailSwapLoadingId(null);
    }
  }

  function formatLastLogin(value) {
    if (value == null || value === '') return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  }

  function toggleSelection(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  const getDisplayRoles = (o) => getUserAdminDisplayRoles(o);

  function applyRelationshipFilter(parentUserId, rel, fromUserId) {
    const pid = String(parentUserId || '').trim();
    if (!pid) return;
    setSearchTerm('');
    setFilterLoginStatus('');
    setFilterRole('');
    setSelectedIds([]);
    const next = { parentUserId: pid, rel };
    const fromId = fromUserId != null ? String(fromUserId).trim() : '';
    if (fromId) next.fromUserId = fromId;
    setSearchParams(next);
  }

  function clearRelationshipFilter() {
    setSearchParams({});
  }

  function renderRolesCell(o) {
    const roles = getDisplayRoles(o);
    if (!roles.length) return '—';
    const linkedParentId = getLinkedParentUserId(o);
    const userId = String(o._id);
    const showParentLink = userHasParentRole(o)
      && (relationshipLookups.linkedChildCountByParentId.get(userId) || 0) > 0;
    const showParticipantParentLink = linkedParentId !== ''
      && relationshipLookups.usersById.has(linkedParentId);

    return roles.map((role, index) => {
      const sep = index > 0 ? ', ' : null;
      let label = role;
      if (role === 'Parent' && showParentLink) {
        label = (
          <button
            type="button"
            className="btn-link"
            title="Show linked children"
            onClick={() => applyRelationshipFilter(o._id, USER_ADMIN_REL_CHILDREN)}
          >
            Parent
          </button>
        );
      } else if (role === 'Participant' && showParticipantParentLink) {
        label = (
          <button
            type="button"
            className="btn-link"
            title="Show parent"
            onClick={() => applyRelationshipFilter(linkedParentId, USER_ADMIN_REL_PARENT, o._id)}
          >
            Participant
          </button>
        );
      }
      return (
        <React.Fragment key={`${o._id}-${role}-${index}`}>
          {sep}
          {label}
        </React.Fragment>
      );
    });
  }

  function renderUserAdminColumnCell(colId, o) {
    switch (colId) {
      case 'name':
        return formatUserTableName(o);
      case 'email_id':
        return displayEmailOrDash(o.email_id);
      case 'roles':
        return renderRolesCell(o);
      case 'login_status':
        return o.login_status ?? 'No Login';
      case 'email_verified':
        return o.email_verified ? 'Yes' : 'No';
      case 'phone':
        return formatPhoneDisplay(o.phone_number);
      case 'last_login':
        return formatLastLogin(o.last_login);
      case 'created':
        return formatLastLogin(o.created_date);
      default:
        return '';
    }
  }

  const searchLower = (searchTerm || '').trim().toLowerCase();
  const filteredBySearch = searchLower
    ? CurrentObjs.filter(o => {
        const first = (o.first_name ?? '').toLowerCase();
        const last = (o.last_name ?? '').toLowerCase();
        const fullName = `${first} ${last}`.trim();
        const email = (o.email_id ?? '').toLowerCase();
        const phone = (o.phone_number ?? '').toLowerCase();
        const roleStr = getDisplayRoles(o).join(' ').toLowerCase();
        const loginStatusStr = (o.login_status ?? 'No Login').toLowerCase();
        return first.includes(searchLower) || last.includes(searchLower) || fullName.includes(searchLower) || email.includes(searchLower) ||
          phone.includes(searchLower) || roleStr.includes(searchLower) || loginStatusStr.includes(searchLower);
      })
    : CurrentObjs;
  const filteredByLoginStatus = filterLoginStatus
    ? filteredBySearch.filter(o => (o.login_status ?? 'No Login') === filterLoginStatus)
    : filteredBySearch;
  const filteredByRole = filterRole
    ? filteredByLoginStatus.filter(o => getDisplayRoles(o).includes(filterRole))
    : filteredByLoginStatus;
  const filteredObjs = hasRelationshipFilter
    ? (filterRel === USER_ADMIN_REL_CHILDREN
      ? filteredByRole.filter((o) => getLinkedParentUserId(o) === filterParentUserId)
      : filteredByRole.filter((o) => String(o._id) === filterParentUserId))
    : filteredByRole;
  const sortedObjs = sortUserAdminRows(filteredObjs, sortConfig);
  const selectedVisibleIds = intersectSelectedIdsWithRows(new Set(selectedIds), filteredObjs);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedVisibleIdList = Array.from(selectedVisibleIds);
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  const relationshipFilterBanner = (() => {
    if (!hasRelationshipFilter) return null;
    const anchorUser = filterFromUserId
      ? CurrentObjs.find((u) => String(u._id) === filterFromUserId)
      : null;
    const parentUser = CurrentObjs.find((u) => String(u._id) === filterParentUserId);
    const parentName = parentUser ? formatUserTableName(parentUser) : 'Unknown user';

    if (filterRel === USER_ADMIN_REL_CHILDREN) {
      const childCount = filteredObjs.length;
      return `Showing ${childCount} linked child${childCount === 1 ? '' : 'ren'} of ${parentName}`;
    }
    if (filterRel === USER_ADMIN_REL_PARENT) {
      const fromName = anchorUser ? formatUserTableName(anchorUser) : null;
      if (fromName && fromName !== '—') {
        return `Showing parent of ${fromName}: ${parentName}`;
      }
      return `Showing parent: ${parentName}`;
    }
    return null;
  })();

  function toggleSelectAll() {
    const allIds = filteredObjs.map(o => o._id);
    const allSelected = allIds.length > 0 && selectedIds.length === allIds.length;
    setSelectedIds(allSelected ? [] : allIds);
  }

  function clearSelection() {
    setSelectedIds([]);
  }

  function parseUserCSV(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);
    const firstIdx = findCsvColumnIndex(headers, ['first_name', 'firstname', 'first']);
    const lastIdx = findCsvColumnIndex(headers, ['last_name', 'lastname', 'last']);
    const emailIdx = findCsvColumnIndex(headers, ['email', 'email_id', 'e-mail']);
    const phoneIdx = findCsvColumnIndex(headers, ['phone', 'phone_number', 'phonenumber']);
    const roleIdx = findCsvColumnIndex(headers, ['role']);
    if (firstIdx === -1 || lastIdx === -1 || emailIdx === -1 || phoneIdx === -1 || roleIdx === -1) {
      throw new Error('CSV must contain columns: first_name, last_name, email (or email_id), phone (or phone_number), role. All required.');
    }
    const records = [];
    for (const values of rows) {
      const maxIdx = Math.max(firstIdx, emailIdx, phoneIdx, roleIdx, lastIdx >= 0 ? lastIdx : 0);
      if (values.length > maxIdx) {
        const roleCell = (values[roleIdx] || '').trim();
        const roles = roleCell ? roleCell.split(/[,;]/).map(r => r.trim()).filter(Boolean) : [];
        records.push({
          first_name: values[firstIdx] || '',
          last_name: lastIdx >= 0 ? (values[lastIdx] || '') : '',
          email_id: values[emailIdx] || '',
          phone_number: values[phoneIdx] || '',
          roles,
        });
      }
    }
    return records;
  }

  async function importFromCSV(file) {
    if (!file) {
      setConfirmDialog({ open: true, title: "Import", message: "Please select a CSV file", mode: "alert", onConfirm: null });
      return;
    }
    const text = await file.text();
    await importFromCsvText(text);
  }

  async function importFromCsvText(text) {
    setImporting(true);
    try {
      const records = parseUserCSV(text);
      if (records.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV file", mode: "alert", onConfirm: null });
        setImporting(false);
        return;
      }
      const validRoles = new Set((RoleObjs || []).map(r => r.role));
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      for (let i = 0; i < records.length; i++) {
        const rec = records[i];
        const recRoles = Array.isArray(rec.roles) ? rec.roles : [];
        if (!rec.first_name || !rec.last_name || !rec.email_id || !rec.phone_number || recRoles.length === 0) {
          errorCount++;
          errors.push(`Row ${i + 2}: Missing required fields (first name, last name, email, phone, at least one role)`);
          continue;
        }
        const invalidRole = recRoles.find(r => !validRoles.has(r));
        if (invalidRole) {
          errorCount++;
          errors.push(`Row ${i + 2}: Invalid role "${invalidRole}"`);
          continue;
        }
        const emailCheck = validateEmail(rec.email_id);
        if (!emailCheck.valid) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${emailCheck.message}`);
          continue;
        }
        const phoneCheck = validatePhone(rec.phone_number);
        if (!phoneCheck.valid) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${phoneCheck.message}`);
          continue;
        }
        try {
          const response = await apiFetch(serverUrl + '/createUser', {
            method: 'POST',
            body: JSON.stringify({
              account_id,
              first_name: rec.first_name,
              last_name: rec.last_name || '',
              email_id: rec.email_id,
              phone_number: rec.phone_number,
              roles: recRoles,
            }),
          });
          const data = await response.text();
          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            const msg = (() => { try { const j = JSON.parse(data); return j.message || data; } catch { return data; } })();
            errors.push(`Row ${i + 2}: ${msg}`);
          }
        } catch (err) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${getMessageForFetchError(err)}`);
        }
      }
      let message = `Import completed!\nSuccess: ${successCount}\nErrors: ${errorCount}`;
      if (errors.length > 0 && errors.length <= 10) {
        message += '\n\nErrors:\n' + errors.join('\n');
      } else if (errors.length > 10) {
        message += '\n\nFirst 10 errors:\n' + errors.slice(0, 10).join('\n');
      }
      setConfirmDialog({ open: true, title: "Import completed", message, mode: "alert", onConfirm: null });
      getDocument();
      getRoleList();
    } catch (err) {
      setBannerError(getMessageForFetchError(err, 'Error importing CSV.'));
    } finally {
      setImporting(false);
    }
  }

  function handleFileChange(e) {
    attachValidatedCsvFileImport(e, importFromCSV, (msg) => {
      setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
    });
  }

  function downloadSampleCSV() {
    downloadCsvTextFile('user-admin-sample.csv', [
      USER_ADMIN_CSV_IMPORT_HEADER_LINE,
      'Jane,Doe,jane@example.com,555-0100,User',
      'John,Smith,john@example.com,555-0101,Admin',
      'Alex,Lead,alex@example.com,555-0102,"Participant,ProgramLead"',
    ]);
  }

  function exportSelectedUsersCsv() {
    if (selectedVisibleCount === 0) {
      setConfirmDialog({
        open: true,
        title: "Export",
        message: "Select at least one user to export.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setExporting(true);
    try {
      const rows = sortedObjs.filter((row) => row && selectedVisibleIds.has(row._id));
      if (rows.length === 0) {
        setConfirmDialog({
          open: true,
          title: "Export",
          message: "No matching users found for the current selection.",
          mode: "alert",
          onConfirm: null,
        });
        return;
      }
      const csv = buildUserAdminCsvExportText(rows);
      const datePart = new Date().toISOString().slice(0, 10);
      downloadCsvTextFromString(`user-admin-export-${datePart}.csv`, csv);
      setBannerError("");
    } catch (err) {
      setBannerError(getMessageForFetchError(err, "Error exporting CSV."));
    } finally {
      setExporting(false);
    }
  }

  const selectedNoLogin = filteredObjs.filter(o => selectedVisibleIds.has(o._id) && (o.login_status === 'No Login' || o.login_status == null));
  const selectedInvited = filteredObjs.filter(o => selectedVisibleIds.has(o._id) && o.login_status === 'Invited');
  const selectedWithEmail = filteredObjs.filter(
    (o) => selectedVisibleIds.has(o._id) && !!(o.email_id && String(o.email_id).trim())
  );

  async function bulkDelete() {
    if (selectedVisibleCount === 0) return;
    const idsToDelete = selectedVisibleIdList;
    setConfirmDialog({
      open: true,
      title: "Delete users",
      message: `Delete ${idsToDelete.length} selected user(s)?\n\nThis also removes their login from authentication if present.\n\nThis cannot be undone.`,
      mode: "confirm",
      confirmLabel: "Delete",
      onConfirm: async () => {
        setBulkActionLoading('delete');
        try {
          const results = await Promise.allSettled(
            idsToDelete.map(id =>
              apiFetch(serverUrl + '/deleteUser', {
                method: 'POST',
                body: JSON.stringify({ account_id, _id: id }),
              }).then(async (r) => ({ ok: r.ok, text: await r.text() }))
            )
          );
          const failed = results.filter(r => r.status === 'rejected' || (r.value && !r.value.ok));
          const successCount = idsToDelete.length - failed.length;
          getDocument();
          setSelectedIds([]);
          if (failed.length > 0) {
            setBannerError(`Deleted ${successCount} user(s). ${failed.length} failed.`);
          } else {
            setConfirmDialog({ open: true, title: "Delete users", message: `Deleted ${idsToDelete.length} user(s) successfully.`, mode: "alert", onConfirm: null });
          }
        } catch (err) {
          setBannerError(getMessageForFetchError(err));
        } finally {
          setBulkActionLoading(null);
        }
      },
    });
  }

  async function bulkInvite() {
    const toInvite = selectedNoLogin.map(o => o._id);
    if (toInvite.length === 0) return;
    setBulkActionLoading('invite');
    try {
      const results = await Promise.allSettled(
        toInvite.map(id =>
          apiFetch(serverUrl + '/inviteUser', {
            method: 'POST',
            body: JSON.stringify({ account_id, _id: id }),
          }).then(async (r) => ({ ok: r.ok, data: await r.json().catch(() => ({})) }))
        )
      );
      const okCount = results.filter(r => r.status === 'fulfilled' && r.value && r.value.ok).length;
      getDocument();
      setSelectedIds([]);
      if (okCount < toInvite.length) {
        setBannerError(`Invite sent to ${okCount} of ${toInvite.length} user(s).`);
      } else {
        setConfirmDialog({ open: true, title: "Invite users", message: `Invite sent to ${okCount} user(s).`, mode: "alert", onConfirm: null });
      }
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    } finally {
      setBulkActionLoading(null);
    }
  }

  async function bulkResend() {
    const toResend = selectedInvited.map(o => o._id);
    if (toResend.length === 0) return;
    setBulkActionLoading('resend');
    try {
      const results = await Promise.allSettled(
        toResend.map(id =>
          apiFetch(serverUrl + '/resendInvite', {
            method: 'POST',
            body: JSON.stringify({ account_id, _id: id }),
          }).then(async (r) => ({ ok: r.ok, data: await r.json().catch(() => ({})) }))
        )
      );
      const okCount = results.filter(r => r.status === 'fulfilled' && r.value && r.value.ok).length;
      getDocument();
      setSelectedIds([]);
      if (okCount < toResend.length) {
        setBannerError(`Resend invite to ${okCount} of ${toResend.length} user(s).`);
      } else {
        setConfirmDialog({ open: true, title: "Resend invites", message: `Resend invite to ${okCount} user(s).`, mode: "alert", onConfirm: null });
      }
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    } finally {
      setBulkActionLoading(null);
    }
  }

  async function bulkSendVerificationCode() {
    const toSend = selectedWithEmail.map((o) => o._id);
    if (toSend.length === 0) return;
    setBulkActionLoading('verification');
    try {
      const results = await Promise.allSettled(
        toSend.map((id) => sendVerificationCodeApi({ account_id, _id: id }))
      );
      const okCount = results.filter((r) => r.status === 'fulfilled').length;
      getDocument();
      setSelectedIds([]);
      if (okCount < toSend.length) {
        setBannerError(`Verification code sent to ${okCount} of ${toSend.length} user(s).`);
      } else {
        setConfirmDialog({
          open: true,
          title: 'Send verification codes',
          message: `Verification code sent to ${okCount} user(s).`,
          mode: 'alert',
          onConfirm: null,
        });
      }
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    } finally {
      setBulkActionLoading(null);
    }
  }

  return (
    <div className="page-container page-container-full">
      <div className="page-content">
        <div className="page-top">
          <h2 className="page-title">User Admin</h2>
          <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />
        <ColumnCustomizerModal
          open={columnModalOpen}
          onClose={() => setColumnModalOpen(false)}
          allColumns={USER_ADMIN_TABLE_COLUMNS}
          visibleIds={visibleColumnIds}
          onChangeVisibleIds={setVisibleColumnIds}
          saving={columnPrefsSaving}
          onReset={async () => {
            const defaultIds = [...USER_ADMIN_TABLE_DEFAULT_VISIBLE];
            setVisibleColumnIds(defaultIds);
            if (!account_id || !userInfo?._id) {
              setColumnModalOpen(false);
              return;
            }
            try {
              setColumnPrefsSaving(true);
              await saveTablePreferences(account_id, userInfo._id, USER_ADMIN_TABLE_KEY, defaultIds);
            } catch (err) {
              setConfirmDialog({
                open: true,
                title: 'User Admin columns',
                message: err && err.message ? err.message : 'Failed to reset column preferences',
                mode: 'alert',
                onConfirm: null,
              });
            } finally {
              setColumnPrefsSaving(false);
            }
            setColumnModalOpen(false);
          }}
          onSave={async (ids) => {
            if (!account_id || !userInfo?._id) {
              setVisibleColumnIds(ids);
              setColumnModalOpen(false);
              return;
            }
            try {
              setColumnPrefsSaving(true);
              await saveTablePreferences(account_id, userInfo._id, USER_ADMIN_TABLE_KEY, ids);
              setVisibleColumnIds(ids);
              setColumnModalOpen(false);
            } catch (err) {
              setConfirmDialog({
                open: true,
                title: 'User Admin columns',
                message: err && err.message ? err.message : 'Failed to save column preferences',
                mode: 'alert',
                onConfirm: null,
              });
            } finally {
              setColumnPrefsSaving(false);
            }
          }}
        />
        <ConfirmationModal
          open={confirmDialog.open}
          title={confirmDialog.title || "Confirm"}
          message={confirmDialog.message}
          mode={confirmDialog.mode === "alert" ? "alert" : "confirm"}
          confirmLabel={
            confirmDialog.mode === "alert"
              ? "OK"
              : (confirmDialog.confirmLabel || undefined)
          }
          onConfirm={() => {
            const fn = confirmDialog.onConfirm;
            setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", confirmLabel: undefined, onConfirm: null });
            if (fn) fn();
          }}
          onCancel={() =>
            setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", confirmLabel: undefined, onConfirm: null })
          }
        />
        <UserAdminModal
          open={userModal.open}
          mode={userModal.mode}
          user={userModal.user}
          roleObjs={RoleObjs}
          onCancel={() => setUserModal({ open: false, mode: 'create', user: null })}
          onSubmit={handleUserModalSubmit}
        />
        <UserEmailSwapModal
          open={emailSwapModal.open}
          user={emailSwapModal.user}
          onCancel={() => setEmailSwapModal({ open: false, user: null })}
          onSubmit={handleEmailSwapSubmit}
        />
        <ImportCsvPanel
          expanded={importPanelExpanded}
          fileInputId="user-admin-csv-import"
          importing={importing}
          columnHint="Columns: first_name, last_name, email, phone, role (header row required). Role can be multiple comma- or semicolon-separated (e.g. Participant,ProgramLead)."
          onDownloadSample={downloadSampleCSV}
          onFileChange={handleFileChange}
          onImportText={importFromCsvText}
        />
        <div style={{ margin: 0 }}>
          <div className="toolbar">
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search name, email, phone, role, login status…" className="input input-max-400" />
            <button type="button" onClick={() => setSearchTerm('')} className="btn btn-secondary btn-sm">Clear</button>
            <select value={filterLoginStatus} onChange={(e) => setFilterLoginStatus(e.target.value)} className="input select input-min-120" title="Filter by login status">
              <option value="">Login Status…</option>
              <option value="No Login">No Login</option>
              <option value="Invited">Invited</option>
              <option value="Active">Active</option>
            </select>
            <div className="input-min-100" style={{ minWidth: 140 }}>
              <SearchableSelect
                value={filterRole}
                onChange={(v) => setFilterRole(String(v || ""))}
                options={(RoleObjs || []).map((r) => ({ value: String(r.role), label: String(r.role) }))}
                placeholder="Role…"
                searchPlaceholder="Search role…"
                allowEmpty={true}
                emptyLabel="Role…"
                maxVisibleItems={4}
                dropdownPortal={true}
                title="Filter by role"
              />
            </div>
            <span className="toolbar-divider" />
            <button type="button" onClick={() => { setImportPanelExpanded(!importPanelExpanded); if (!importPanelExpanded) setUserModal({ open: false, mode: 'create', user: null }); }} className={`btn btn-md ${importPanelExpanded ? 'btn-primary-outline' : 'btn-primary'}`}>{importPanelExpanded ? '− Import CSV' : '+ Import CSV'}</button>
            <button
              type="button"
              className="btn btn-md btn-primary-outline"
              disabled={selectedVisibleCount === 0 || exporting}
              onClick={() => exportSelectedUsersCsv()}
            >
              {exporting ? "Exporting…" : `Export CSV${selectedCountSuffix}`}
            </button>
            <button type="button" onClick={() => { setImportPanelExpanded(false); setUserModal({ open: true, mode: 'create', user: null }); }} className="btn btn-md btn-primary">+ New user</button>
          </div>
          {relationshipFilterBanner && (
            <div className="toolbar">
              <span className="toolbar-label">{relationshipFilterBanner}</span>
              <button type="button" onClick={clearRelationshipFilter} className="btn btn-secondary btn-sm">Clear</button>
            </div>
          )}
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {selectedVisibleCount > 0 && (
          <div className="toolbar">
            <span className="toolbar-label">{selectedVisibleCount} selected</span>
            <button
              type="button"
              onClick={bulkDelete}
              disabled={!!bulkActionLoading}
              className="btn btn-danger"
            >
              {bulkActionLoading === 'delete' ? 'Deleting…' : `Delete selected${selectedCountSuffix}`}
            </button>
            <button
              type="button"
              onClick={bulkInvite}
              disabled={!!bulkActionLoading || selectedNoLogin.length === 0}
              className="btn btn-primary"
            >
              {bulkActionLoading === 'invite' ? 'Sending…' : `Invite selected (${selectedNoLogin.length})`}
            </button>
            <button
              type="button"
              onClick={bulkResend}
              disabled={!!bulkActionLoading || selectedInvited.length === 0}
              className="btn btn-primary"
            >
              {bulkActionLoading === 'resend' ? 'Sending…' : `Resend selected (${selectedInvited.length})`}
            </button>
            <button
              type="button"
              onClick={bulkSendVerificationCode}
              disabled={!!bulkActionLoading || selectedWithEmail.length === 0}
              className="btn btn-primary"
            >
              {bulkActionLoading === 'verification'
                ? 'Sending…'
                : `Send verification selected (${selectedWithEmail.length})`}
            </button>
            <button type="button" onClick={clearSelection} className="btn btn-secondary">
              Clear selection
            </button>
          </div>
        )}
        <div className="table-scroll">
        <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th className="text-center">
                <input
                  type="checkbox"
                  checked={filteredObjs.length > 0 && selectedIds.length === filteredObjs.length}
                  onChange={toggleSelectAll}
                  title="Select all"
                />
              </th>
              {visibleColumnIds.map((colId) => {
                const col = USER_ADMIN_TABLE_COLUMNS.find((c) => c.id === colId);
                return (
                  <SortableTableTh
                    key={colId}
                    sortKey={colId}
                    currentSortKey={sortConfig.key}
                    direction={sortConfig.direction}
                    onSort={handleSort}
                  >
                    {col ? col.label : colId}
                  </SortableTableTh>
                );
              })}
              <TableActionColumnHeader
                align="left"
                onCustomizeColumns={() => setColumnModalOpen(true)}
                customizeDisabled={columnPrefsSaving}
              />
            </tr>
          </thead>
          <tbody>
            {sortedObjs.map(currentObj => (
                <tr key={currentObj._id}>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(currentObj._id)}
                      onChange={() => toggleSelection(currentObj._id)}
                      title="Select row"
                    />
                  </td>
                  {visibleColumnIds.map((colId) => (
                    <td key={colId}>{renderUserAdminColumnCell(colId, currentObj)}</td>
                  ))}
                  <td>
                    <RowActionIcons>
                      <button type="button" onClick={() => { setImportPanelExpanded(false); setUserModal({ open: true, mode: 'edit', user: currentObj }); }} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                      {!!(currentObj.email_id && String(currentObj.email_id).trim() && !isPlaceholderEmail(currentObj.email_id)) && (
                        <button
                          type="button"
                          onClick={() => { setImportPanelExpanded(false); setEmailSwapModal({ open: true, user: currentObj }); }}
                          disabled={emailSwapLoadingId === currentObj._id}
                          className="btn-link"
                          style={iconBtnStyle}
                          title="Swap email"
                        >
                          <IconSwapEmail size={18} />
                        </button>
                      )}
                      {(currentObj.login_status === 'No Login' || currentObj.login_status == null) && (
                        <button
                          type="button"
                          onClick={() => inviteUser(currentObj)}
                          disabled={inviteLoadingId === currentObj._id}
                          className="btn-link"
                          style={iconBtnStyle}
                          title="Invite"
                        >
                          {inviteLoadingId === currentObj._id ? 'Sending…' : <IconInvite size={18} />}
                        </button>
                      )}
                      {(currentObj.login_status === 'Invited') && (
                        <button
                          type="button"
                          onClick={() => resendInvite(currentObj)}
                          disabled={resendLoadingId === currentObj._id}
                          className="btn-link"
                          style={iconBtnStyle}
                          title="Resend"
                        >
                          {resendLoadingId === currentObj._id ? 'Sending…' : <IconInvite size={18} />}
                        </button>
                      )}
                      {!!(currentObj.email_id && String(currentObj.email_id).trim()) && (
                        <button
                          type="button"
                          onClick={() => sendVerificationCode(currentObj)}
                          disabled={verificationLoadingId === currentObj._id}
                          className="btn-link"
                          style={iconBtnStyle}
                          title="Send verification code"
                        >
                          {verificationLoadingId === currentObj._id ? 'Sending…' : <IconVerificationCode size={18} />}
                        </button>
                      )}
                      <button type="button" onClick={() => deleteDocument(currentObj)} className="btn-link-danger" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                    </RowActionIcons>
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
        </div>
        </div>
        </div>
      </div>
    </div>
  );
}
export default UserAdmin